import React from 'react';
import styles from './Pagination.module.scss';

interface PaginationProps {
  page: number;          // 1-based current page
  total: number;         // total item count
  pageSize: number;      // items per page
  onChange: (page: number) => void;
  className?: string;
}

/** Compact, reusable pager used across the customer browse flow (organizers, branches, rooms, stays). */
const Pagination: React.FC<PaginationProps> = ({ page, total, pageSize, onChange, className }) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (pageCount <= 1) return null;

  // Window of page numbers around the current page (max 5 shown).
  const windowSize = 5;
  let start = Math.max(1, page - Math.floor(windowSize / 2));
  const end = Math.min(pageCount, start + windowSize - 1);
  start = Math.max(1, end - windowSize + 1);
  const pages: number[] = [];
  for (let i = start; i <= end; i++) pages.push(i);

  const go = (p: number) => { if (p >= 1 && p <= pageCount && p !== page) onChange(p); };

  return (
    <nav className={`${styles.pager} ${className || ''}`} aria-label="Pagination">
      <button className={styles.navBtn} onClick={() => go(page - 1)} disabled={page <= 1} aria-label="Previous page">‹</button>
      {start > 1 && (
        <>
          <button className={styles.pageBtn} onClick={() => go(1)}>1</button>
          {start > 2 && <span className={styles.ellipsis}>…</span>}
        </>
      )}
      {pages.map((p) => (
        <button
          key={p}
          className={`${styles.pageBtn} ${p === page ? styles.active : ''}`}
          onClick={() => go(p)}
          aria-current={p === page ? 'page' : undefined}
        >
          {p}
        </button>
      ))}
      {end < pageCount && (
        <>
          {end < pageCount - 1 && <span className={styles.ellipsis}>…</span>}
          <button className={styles.pageBtn} onClick={() => go(pageCount)}>{pageCount}</button>
        </>
      )}
      <button className={styles.navBtn} onClick={() => go(page + 1)} disabled={page >= pageCount} aria-label="Next page">›</button>
    </nav>
  );
};

export default Pagination;
